import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { isShapedWall } from '@/components/climbs/board-grid/geometry';
import { devicesService } from '../../services/devices';
import {
  CIRCUIT_HIGHLIGHT_COLORS,
  CIRCUIT_HIGHLIGHT_CYCLE,
} from '@/components/climbs/board-grid/roles';
import type {
  Board,
  BoardPayload,
  BoardType,
  CircuitHighlightColor,
  Device,
  GradingSystemRef,
} from '../../types/device';
import BoardImageUpload from './BoardImageUpload';

interface BoardFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gymSlug: string;
  board: Board | null;
  boardTypes: BoardType[];
  gradingSystems: GradingSystemRef[];
  devices: Device[];
  onSaved: (board: Board) => void;
}

interface FormState {
  board_type_id: string;
  name: string;
  device_id: string;
  led_kit: boolean;
  visible: boolean;
  grading_system_id: string;
  circuit_highlight_color: CircuitHighlightColor;
  inclination: string;
  hold_sets: string[];
  image_signed_id: string | null;
  previewImageUrl: string | null;
}

const EMPTY: FormState = {
  board_type_id: '',
  name: '',
  device_id: '',
  led_kit: false,
  visible: true,
  grading_system_id: '',
  circuit_highlight_color: CIRCUIT_HIGHLIGHT_CYCLE[0],
  inclination: '',
  hold_sets: [],
  image_signed_id: null,
  previewImageUrl: null,
};

/**
 * Add/edit form for one gym board: what it is, what it is called, and how it
 * is lit — a device for a wall wired with our LED strip (a spray wall, a Grid
 * panel), the LED kit for a Moonboard or a Kilter, which the climber's own
 * phone lights over Web Bluetooth. How the LEDs look (brightness, the
 * circuit's colour, the wiring) is set on the board's own page, next to the
 * wall it is about.
 */
export default function BoardFormDialog({
  open,
  onOpenChange,
  gymSlug,
  board,
  boardTypes,
  gradingSystems,
  devices,
  onSaved,
}: BoardFormDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(
      board
        ? {
            board_type_id: board.board_type.id,
            name: board.name,
            device_id: board.device_id ?? '',
            led_kit: board.led_kit,
            visible: board.visible,
            grading_system_id: board.grading_system?.id ?? '',
            circuit_highlight_color: board.resolved_circuit_highlight_color,
            inclination: board.inclination?.toString() ?? '',
            // Resolved, not raw: a board nobody has configured yet counts as
            // having every set its type offers, and that is what the tick boxes
            // should show before anything is unticked.
            hold_sets: board.resolved_hold_sets ?? [],
            image_signed_id: null,
            previewImageUrl: board.image_url,
          }
        : EMPTY
    );
  }, [open, board]);

  const selectedType = useMemo(
    () => boardTypes.find((bt) => bt.id === form.board_type_id) ?? null,
    [boardTypes, form.board_type_id]
  );

  const needsGradeSystem = !!selectedType && !selectedType.grading_system;
  // **A device drives a wired strip and nothing else**: a Moonboard or a
  // Kilter is lit from the climber's own phone, so all it asks is whether the
  // wall has its LED kit.
  const wired = selectedType?.connectivity === 'wired';
  const bluetooth = selectedType?.connectivity === 'bluetooth';
  // The circuit's current hold can only be given a colour of its own where we
  // choose the colours: a Moonboard is sent letters and paints them itself.
  // Asked here for a Kilter only — it has no page of its own to describe its
  // wall on; a wired wall sets it on its board's page.
  const showHighlightColor = bluetooth && form.led_kit && selectedType?.light_protocol === 'kilter';
  // A wall drawn as lines has no grid to ask about: its holds are the ones
  // strung along the lines. Said once, on the board's own page's behalf.
  const drawnAsLines = isShapedWall(board?.shapes);
  // A board that can change angle grades per climb, so there is no board-wide
  // angle to set; every other board hangs at whatever angle this gym built it.
  const showInclination = !!selectedType && !selectedType.adjustable_inclination;
  // Layouts sold as a wall plus a choice of hold sets (Moonboard) ask which
  // ones this gym actually bought; the rest have nothing to choose.
  const holdSets = useMemo(() => selectedType?.hold_sets ?? [], [selectedType]);
  // A mapped layout (Kilter) ships the manufacturer's own picture with the
  // holds placed against its edges. There is nothing for a gym to upload or
  // align there — a photo of their wall would only move every hold — so the
  // upload goes away and the type's picture is shown as what will be used.
  const layoutFromType = !!selectedType?.mapped_layout;

  const canSave = form.board_type_id.length > 0 && form.name.trim().length > 0;

  // Picking a type seeds what that type suggests, so an admin only changes
  // what their own wall does differently. Anything already filled in is kept.
  const handleTypeChange = (value: string) => {
    const picked = boardTypes.find((bt) => bt.id === value) ?? null;

    setForm((prev) => ({
      ...prev,
      board_type_id: value,
      // A device belongs to a wired wall, the LED kit to a Moonboard or a
      // Kilter: neither carries over to the other kind.
      device_id: picked?.connectivity === 'wired' ? prev.device_id : '',
      led_kit: picked?.connectivity === 'bluetooth' ? prev.led_kit : false,
      inclination: prev.inclination || (picked?.inclination?.toString() ?? ''),
      // Every set the new type offers, ticked: a board is assumed complete
      // until its gym says which ones it is missing.
      hold_sets: picked?.hold_sets ?? [],
    }));
  };

  const toggleHoldSet = (set: string, checked: boolean) => {
    setForm((prev) => ({
      ...prev,
      // Rebuilt from the type's list rather than appended to, so the stored
      // order always matches the order they are shown in.
      hold_sets: holdSets.filter((s) => (s === set ? checked : prev.hold_sets.includes(s))),
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSave || saving) return;

    setSaving(true);
    try {
      const payload: BoardPayload = {
        board_type_id: form.board_type_id,
        name: form.name.trim(),
        device_id: wired ? form.device_id || null : null,
        led_kit: bluetooth && form.led_kit,
        visible: form.visible,
        grading_system_id: needsGradeSystem ? form.grading_system_id || null : null,
        // Only where it is asked: a wired wall's colour is set on its board's
        // page, and leaving it out keeps it.
        ...(showHighlightColor ? { circuit_highlight_color: form.circuit_highlight_color } : {}),
        // Left empty, the board falls back to its type's angle.
        inclination: showInclination && form.inclination !== '' ? Number(form.inclination) : null,
        // Only sent for the layouts that have sets to choose from; sending []
        // for the others would be storing "none" where "not applicable" is meant.
        ...(holdSets.length > 0 ? { hold_sets: form.hold_sets } : {}),
        // Where the grid sits on the photo is never this form's to send: it is
        // aligned on the board's own page, against a picture big enough to see
        // a hold in. Leaving it out keeps whatever alignment is already stored,
        // so saving a rename here does not move a wall full of problems.
        ...(form.image_signed_id && !layoutFromType
          ? { image_signed_id: form.image_signed_id }
          : {}),
      };

      const saved = board
        ? await devicesService.updateBoard(gymSlug, board.id, payload)
        : await devicesService.createBoard(gymSlug, payload);

      onSaved(saved);
    } catch (error) {
      toast({
        title: board ? t('gymAdmin.boards.updateError') : t('gymAdmin.boards.createError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {board ? t('gymAdmin.boards.editTitle') : t('gymAdmin.boards.newTitle')}
          </DialogTitle>
          <DialogDescription>{t('gymAdmin.boards.formDescription')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>{t('gymAdmin.boards.fields.boardType')}</Label>
            <Select value={form.board_type_id} onValueChange={handleTypeChange}>
              <SelectTrigger>
                <SelectValue placeholder={t('gymAdmin.boards.fields.boardTypePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {boardTypes.map((bt) => (
                  <SelectItem key={bt.id} value={bt.id}>
                    {bt.name}
                    {bt.global ? ` · ${t('gymAdmin.boards.global')}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="board-name">{t('gymAdmin.boards.fields.name')}</Label>
            <Input
              id="board-name"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            />
          </div>

          {/* Hidden, the board is the staff's alone: not on the gym profile,
              its problems and its lights closed to the members. */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id="board-visible"
                checked={form.visible}
                onCheckedChange={(checked) =>
                  setForm((prev) => ({ ...prev, visible: checked === true }))
                }
              />
              <Label htmlFor="board-visible" className="font-normal">
                {t('gymAdmin.boards.fields.visible')}
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('gymAdmin.boards.fields.visibleHelp')}
            </p>
          </div>

          {/* How the wall is lit: a wired strip is driven by a device of
              ours; a Moonboard or a Kilter by the climber's own phone, once
              the gym says the wall has its LED kit. */}
          {wired && (
            <div className="space-y-2">
              <Label>{t('gymAdmin.boards.fields.device')}</Label>
              <Select
                value={form.device_id || 'none'}
                onValueChange={(value) =>
                  setForm((prev) => ({ ...prev, device_id: value === 'none' ? '' : value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('gymAdmin.boards.fields.noDevice')}</SelectItem>
                  {devices.map((device) => (
                    <SelectItem key={device.id} value={device.id}>
                      {device.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t('gymAdmin.boards.fields.deviceHelp')}
              </p>
            </div>
          )}

          {bluetooth && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="board-led-kit"
                  checked={form.led_kit}
                  onCheckedChange={(checked) =>
                    setForm((prev) => ({ ...prev, led_kit: checked === true }))
                  }
                />
                <Label htmlFor="board-led-kit" className="font-normal">
                  {t('gymAdmin.boards.fields.ledKit')}
                </Label>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('gymAdmin.boards.fields.ledKitHelp')}
              </p>
            </div>
          )}

          {needsGradeSystem && (
            <div className="space-y-2">
              <Label>{t('gymAdmin.boards.fields.gradingSystem')}</Label>
              <Select
                value={form.grading_system_id}
                onValueChange={(value) =>
                  setForm((prev) => ({ ...prev, grading_system_id: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('gymAdmin.boards.fields.gradingSystemPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {gradingSystems.map((gs) => (
                    <SelectItem key={gs.id} value={gs.id}>
                      {gs.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Which bags of holds are actually on this wall. The problem library
              is shared across every gym with this layout, so it covers sets a
              given wall may not have — untick those and they drop off the tab. */}
          {holdSets.length > 0 && (
            <div className="space-y-2">
              <Label>{t('gymAdmin.boards.fields.holdSets')}</Label>
              <div className="space-y-2 rounded-md border p-3">
                {holdSets.map((set) => (
                  <div key={set} className="flex items-center gap-2">
                    <Checkbox
                      id={`board-hold-set-${set}`}
                      checked={form.hold_sets.includes(set)}
                      onCheckedChange={(checked) => toggleHoldSet(set, checked === true)}
                    />
                    <Label htmlFor={`board-hold-set-${set}`} className="font-normal">
                      {t(`climbs.board.holdSets.${set}`, { defaultValue: set })}
                    </Label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {t('gymAdmin.boards.fields.holdSetsHelp')}
              </p>
            </div>
          )}

          {/* A wall drawn as lines, said once: the grid fields above it are
              gone, and this is what took their place. */}
          {drawnAsLines && (
            <p className="text-xs text-muted-foreground">
              {t('gymAdmin.boards.fields.drawnAsLines', { count: board?.led_count ?? 0 })}
            </p>
          )}

          {showInclination && (
            <div className="space-y-2">
              <Label htmlFor="board-inclination">{t('gymAdmin.boards.fields.inclination')}</Label>
              <Input
                id="board-inclination"
                type="number"
                min={0}
                max={80}
                value={form.inclination}
                placeholder="—"
                onChange={(e) => setForm((prev) => ({ ...prev, inclination: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                {t('gymAdmin.boards.fields.inclinationHelp')}
              </p>
            </div>
          )}

          {/* Which colour the wall points with while a circuit is played: the
              hold the climber is heading for is lit in it, so it is picked out
              of a problem that is lit whole. */}
          {showHighlightColor && (
            <div className="space-y-2">
              <Label>{t('gymAdmin.boards.fields.circuitHighlightColor')}</Label>
              <Select
                value={form.circuit_highlight_color}
                onValueChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    circuit_highlight_color: value as CircuitHighlightColor,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CIRCUIT_HIGHLIGHT_CYCLE.map((color) => (
                    <SelectItem key={color} value={color}>
                      <span className="flex items-center gap-2">
                        <span
                          className="size-3 rounded-full border"
                          style={{ backgroundColor: CIRCUIT_HIGHLIGHT_COLORS[color] }}
                          aria-hidden
                        />
                        {t(`gymAdmin.boards.circuitHighlightColors.${color}`)}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t('gymAdmin.boards.fields.circuitHighlightColorHelp')}
              </p>
            </div>
          )}

          {layoutFromType ? (
            <div className="space-y-2">
              <Label>{t('gymAdmin.boards.fields.image')}</Label>
              {selectedType?.image_url && (
                <img
                  src={selectedType.image_url}
                  alt=""
                  className="max-h-64 w-full rounded-md border bg-muted object-contain"
                />
              )}
              <p className="text-xs text-muted-foreground">
                {t('gymAdmin.boards.fields.imageFromBoardType', { type: selectedType?.name })}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>{t('gymAdmin.boards.fields.image')}</Label>
              <BoardImageUpload
                currentImageUrl={form.previewImageUrl}
                onUploaded={(signedId, previewUrl) =>
                  setForm((prev) => ({
                    ...prev,
                    image_signed_id: signedId,
                    previewImageUrl: previewUrl,
                  }))
                }
              />
              {/* Aligning the grid is a job for a picture you can see a hold
                  in, which a dialog this size is not. It lives on the board's
                  own page, where the photo gets a resizable half of the window
                  and the problems drawn on it are there to check against. */}
              <p className="text-xs text-muted-foreground">
                {t('gymAdmin.boards.fields.calibrationMoved')}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!canSave || saving}>
              {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
